import { useCallback, useMemo, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Cookie from 'js-cookie'
import { useAppSelector } from '~/store'
import { SettingsLayout, SettingsSection } from './settings-layout'

function useStoresQuery() {
    return useQuery({
        queryKey: ['stores'],
        queryFn: async () => {
            const res = await fetch('/endpoints/stores/')
            if (!res.ok) {
                throw new Error('Failed to fetch stores')
            }

            return res.json()
        },
        staleTime: Infinity,
    })
}

function AddStoreDialog({ open, onClose }) {
    const accountOwnerId = useAppSelector((state) => state.initial.accountOwner.id)
    const [newStoreName, setNewStoreName] = useState('')
    const [newStoreError, setNewStoreError] = useState(null)
    const queryClient = useQueryClient()

    const { mutate: addStore, isLoading } = useMutation({
        mutationFn: async (newStoreData) => {
            const response = await fetch('/endpoints/stores/create/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'X-CSRFToken': Cookie.get('csrftoken'),
                },
                body: JSON.stringify(newStoreData),
            })

            if (!response.ok) {
                throw new Error('Failed to create store')
            }

            return response.json()
        },
    })

    const handleClose = useCallback(() => {
        setNewStoreName('')
        setNewStoreError(null)
        onClose()
    }, [onClose])

    const handleSubmit = () => {
        if (newStoreName.length < 5) {
            setNewStoreError(gettext('Store name must be more than 5 characters.'))
            return
        }

        addStore(
            {
                owner: accountOwnerId,
                name: newStoreName,
                sync_mode: 'receiving',
            },
            {
                onSuccess: async () => {
                    await queryClient.invalidateQueries({ queryKey: ['stores'] })
                    handleClose()
                },
                onError: (error) => {
                    setNewStoreError(error.message || gettext('Failed to create store'))
                },
            }
        )
    }

    return (
        <Dialog fullWidth maxWidth="sm" open={open}>
            <DialogTitle>{gettext('Add new store')}</DialogTitle>
            <DialogContent>
                <DialogContentText>{gettext('Enter the name of a new store')}</DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    margin="dense"
                    disabled={isLoading}
                    label={pgettext('Store name')}
                    value={newStoreName}
                    error={Boolean(newStoreError)}
                    helperText={newStoreError}
                    inputProps={{ maxLength: 50 }}
                    onChange={(e) => setNewStoreName(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                    disabled={isLoading}
                    onClick={handleClose}
                >
                    {gettext('Cancel')}
                </Button>
                <LoadingButton
                    color="secondary"
                    disabled={isLoading}
                    loading={isLoading}
                    onClick={handleSubmit}
                >
                    {gettext('Add')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

function DeleteConfirmationDialog({ open, storeId, onClose }) {
    const { data: stores } = useStoresQuery()
    const store = useMemo(() => stores?.find((store) => store.id === storeId), [stores, storeId])

    const [confirmationText, setConfirmationText] = useState('')
    const [deleteError, setDeleteError] = useState(null)
    const queryClient = useQueryClient()

    const { mutate: deleteStore, isLoading } = useMutation({
        mutationFn: async (storeId) => {
            const response = await fetch(`/endpoints/stores/${storeId}/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'X-CSRFToken': Cookie.get('csrftoken'),
                },
            })

            if (!response.ok) {
                throw new Error('Failed to delete store')
            }

            return storeId
        },
    })

    const handleClose = useCallback(() => {
        setConfirmationText('')
        setDeleteError(null)
        onClose()
    }, [onClose])

    const handleDelete = () => {
        if (!store) {
            return
        }

        if (confirmationText !== store.name) {
            setDeleteError(gettext('Enter the exact name of your store.'))
            return
        }

        deleteStore(store.id, {
            onSuccess: async () => {
                await queryClient.invalidateQueries({ queryKey: ['stores'] })
                handleClose()
            },
            onError: (error) => {
                setDeleteError(error.message || gettext('Failed to delete store'))
            },
        })
    }

    return (
        <Dialog open={open}>
            <DialogTitle>{gettext('Delete this store?')}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 1 }}>
                    {gettext(
                        'Deleting this store will remove ALL related product and order data from Oakra. Only proceed if you are absolutely sure.'
                    )}
                </DialogContentText>
                <DialogContentText sx={{ mb: 1 }}>
                    {gettext('It will not affect information on your linked channels.')}
                </DialogContentText>
                <DialogContentText sx={{ mb: 1 }}>
                    {interpolate(gettext('Enter "%s" to confirm.'), [store?.name || ''])}
                </DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    margin="dense"
                    size="small"
                    disabled={isLoading}
                    label={pgettext('Store name')}
                    value={confirmationText}
                    error={Boolean(deleteError)}
                    helperText={deleteError}
                    inputProps={{ maxLength: 50 }}
                    onChange={(e) => setConfirmationText(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                    disabled={isLoading}
                    onClick={handleClose}
                >
                    {gettext('Cancel')}
                </Button>
                <LoadingButton
                    color="error"
                    disabled={confirmationText !== store?.name}
                    loading={isLoading}
                    onClick={handleDelete}
                >
                    {gettext('Delete')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export function StoreSettings() {
    const { data: stores, isLoading } = useStoresQuery()
    const [displayAddNewStoreDialog, setDisplayAddNewStoreDialog] = useState(false)

    const handleAddDialogClose = useCallback(() => {
        setDisplayAddNewStoreDialog(false)
    }, [])

    const [displayDeleteConfirmation, setDisplayDeleteConfirmation] = useState(false)
    const [storeIdToDelete, setStoreIdToDelete] = useState(0)

    const handleDeleteClick = (storeId) => {
        setStoreIdToDelete(storeId)
        setDisplayDeleteConfirmation(true)
    }

    const handleDeleteDialogClose = useCallback(() => {
        setDisplayDeleteConfirmation(false)
        setStoreIdToDelete(0)
    }, [])

    return (
        <SettingsLayout>
            <SettingsSection header={gettext('Stores')}>
                <Stack gap={4}>
                    <Stack spacing={1}>
                        <Typography>
                            {gettext(
                                'If you run multiple online stores selling different types of products, add them below.'
                            )}
                        </Typography>
                        <Typography>
                            {gettext(
                                'A store is defined by one type of product. For example, you may have a store selling the same beauty products on both Lazada and Shopee. In Oakra, this is considered one store and you only need to add it once.'
                            )}
                        </Typography>
                        <Typography>
                            {gettext(
                                'Or you may have one Shopee store selling beauty products and another one selling clothes. These are considered two different stores in Oakra. Please add them separately.'
                            )}
                        </Typography>
                    </Stack>

                    <Box sx={{ width: 600 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 1,
                                borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
                            }}
                            key="header"
                        >
                            <Box sx={{ flex: '0 0 300px' }}>
                                <Typography variant="subtitle2">{gettext('Shop')}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex' }}>
                                <Typography variant="subtitle2">
                                    {gettext('Connected channels')}
                                </Typography>
                            </Box>
                            <Box sx={{ flexBasis: 50 }}></Box>
                        </Box>
                        {isLoading
                            ? <Stack spacing={1} sx={{ mt: 2 }}>
                                  {[1, 2, 3].map((index) => (
                                      <Box
                                          key={index}
                                          sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                          }}
                                      >
                                          <Box sx={{ flex: '0 0 300px' }}>
                                              <Skeleton variant="text" width="80%" height={28} />
                                          </Box>
                                          <Box sx={{ display: 'flex', gap: 1 }}>
                                              <Skeleton variant="circular" width={24} height={24} />
                                              <Skeleton variant="circular" width={24} height={24} />
                                          </Box>
                                          <Box sx={{ flexBasis: 50 }}>
                                              <IconButton disabled>
                                                  <DeleteIcon />
                                              </IconButton>
                                          </Box>
                                      </Box>
                                  ))}
                              </Stack>
                            : stores?.map((store) => (
                                  <Box
                                      key={store.id}
                                      sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          py: 1,
                                          '&:not(:last-of-type)': {
                                              borderBottom: (theme) =>
                                                  `1px solid ${theme.palette.grey[300]}`,
                                          },
                                      }}
                                  >
                                      <Box sx={{ flex: '0 0 300px' }}>
                                          <Typography sx={{ fontSize: '1rem' }}>
                                              {store.name}
                                          </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex' }}>
                                          {store.shops.map((shop) => (
                                              <img
                                                  style={{ width: 25, mr: 0.5 }}
                                                  src={shop.channel.channel_square_icon_url}
                                                  key={shop.id}
                                              />
                                          ))}
                                      </Box>
                                      <Box sx={{ flexBasis: 50 }}>
                                          <IconButton
                                              color="secondary"
                                              onClick={() => handleDeleteClick(store.id)}
                                          >
                                              <DeleteIcon />
                                          </IconButton>
                                      </Box>
                                  </Box>
                              ))}
                        <Button
                            variant="outlined"
                            color="secondary"
                            disabled={isLoading}
                            onClick={() => setDisplayAddNewStoreDialog(true)}
                        >
                            <AddIcon />
                            {gettext('Add store')}
                        </Button>
                    </Box>
                </Stack>
            </SettingsSection>

            <AddStoreDialog open={displayAddNewStoreDialog} onClose={handleAddDialogClose} />

            <DeleteConfirmationDialog
                open={displayDeleteConfirmation}
                storeId={storeIdToDelete}
                onClose={handleDeleteDialogClose}
            />
        </SettingsLayout>
    )
}
